import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  Alert,
  TouchableOpacity,
  TextInput,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Product } from '../types';
import { useAuth } from '../context/AuthContext';
import { usePersistentCart } from '../context/PersistentCartContext';
import { useFavorites } from '../context/FavoritesContext';
import Button from '../components/Button';

const { width } = Dimensions.get('window');

interface ProductDetailScreenProps {
  route: RouteProp<{ ProductDetail: { productId: number } }, 'ProductDetail'>;
  navigation: StackNavigationProp<any>;
}

const ProductDetailScreen: React.FC<ProductDetailScreenProps> = ({ 
  route, 
  navigation 
}) => {
  const { productId } = route.params;
  const { user } = useAuth();
  const { addToCart, state } = usePersistentCart();
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  
  // Helper functions para compatibilidad
  const isInCart = (productId: number) => {
    return state.items.some(item => item.id_producto === productId);
  };
  
  const getCartItem = (productId: number) => {
    return state.items.find(item => item.id_producto === productId);
  };
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProductDetail();
  }, [productId]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      
      // Simulación de llamada a la API
      // En una implementación real, esto sería una llamada a tu backend
      const response = await fetch(`http://127.0.0.1:8000/api/products/${productId}`, {
        headers: {
          'Authorization': `Bearer ${await getStoredToken()}`,
          'Accept': 'application/json',
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setProduct(data.data);
      } else {
        throw new Error(data.message || 'Error al cargar el producto');
      }
    } catch (error: any) {
      console.error('Error fetching product:', error);
      Alert.alert('Error', 'No se pudo cargar el producto');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const getStoredToken = async () => {
    // Implementación simplificada para obtener el token
    // En una implementación real, esto vendría de AsyncStorage
    return 'dummy-token';
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    if (quantity > product.stock) {
      Alert.alert('Stock insuficiente', `Solo hay ${product.stock} unidades disponibles`);
      return;
    }

    setIsAddingToCart(true);
    
    try {
      // Convertir product al formato esperado por addToCart
      const cartProduct = {
        id: product.id,
        nombre: product.nombre,
        precio: parseFloat(product.precio), // Convertir string a number
        imagen: product.imagen,
        stock: product.stock
      };
      
      await addToCart(cartProduct, quantity);

      Alert.alert(
        '¡Agregado al carrito!',
        `${product.nombre} (${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}) agregado al carrito`,
        [
          { text: 'Seguir comprando', style: 'cancel' },
          { 
            text: 'Ver carrito', 
            onPress: () => navigation.navigate('Cart')
          }
        ]
      );
    } catch (error) {
      console.error('Error agregando al carrito:', error);
      Alert.alert('Error', 'No se pudo agregar el producto al carrito');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBuyNow = () => {
    if (!product) return;
    
    handleAddToCart();
    navigation.navigate('Cart');
  };

  const handleQuantityChange = (change: number) => {
    if (!product) return;
    
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleToggleFavorite = async () => {
    if (!product) return;
    
    try {
      if (isFavorite(product.id)) {
        await removeFromFavorites(product.id);
      } else {
        await addToFavorites(product);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      Alert.alert('Error', 'No se pudo actualizar favoritos');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#e74c3c" />
          <Text style={styles.loadingText}>Cargando producto...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!product) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Producto no encontrado</Text>
          <Button
            title="Volver"
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          />
        </View>
      </SafeAreaView>
    );
  }

  const isProductInCart = isInCart(product.id);
  const cartItem = getCartItem(product.id);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header con botón de volver */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Text style={styles.backButtonText}>← Volver</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleToggleFavorite} style={styles.favoriteButton}>
            <Text style={styles.favoriteButtonText}>
              {product && isFavorite(product.id) ? '❤️' : '🤍'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Imagen del producto */}
        <View style={styles.imageContainer}>
          <Image 
            source={{ uri: product.imagen || 'https://via.placeholder.com/400' }}
            style={styles.productImage}
            defaultSource={{ uri: 'https://via.placeholder.com/400' }}
          />
        </View>

        {/* Información del producto */}
        <View style={styles.infoContainer}>
          <Text style={styles.productName}>{product.nombre}</Text>
          <Text style={styles.productCategory}>{product.categoria}</Text>
          
          <View style={styles.priceContainer}>
            <Text style={styles.productPrice}>Q{parseFloat(product.precio).toFixed(2)}</Text>
            <View style={styles.stockContainer}>
              <Text style={styles.stockText}>
                Stock: {product.stock} {product.stock === 1 ? 'unidad' : 'unidades'}
              </Text>
            </View>
          </View>

          {/* Descripción */}
          {product.descripcion && (
            <View style={styles.descriptionContainer}>
              <Text style={styles.descriptionTitle}>Descripción</Text>
              <Text style={styles.descriptionText}>{product.descripcion}</Text>
            </View>
          )}

          {/* Información del vendedor */}
          {product.empresa && (
            <View style={styles.sellerContainer}>
              <Text style={styles.sellerTitle}>Vendedor</Text>
              <Text style={styles.sellerName}>{product.empresa.name}</Text>
            </View>
          )}

          {/* Control de cantidad */}
          <View style={styles.quantityContainer}>
            <Text style={styles.quantityLabel}>Cantidad:</Text>
            <View style={styles.quantityControls}>
              <TouchableOpacity
                style={styles.quantityButton}
                onPress={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <Text style={[
                  styles.quantityButtonText,
                  quantity <= 1 && styles.quantityButtonDisabled
                ]}>-</Text>
              </TouchableOpacity>
              
              <TextInput
                style={styles.quantityInput}
                value={quantity.toString()}
                onChangeText={(text) => {
                  const num = parseInt(text) || 1;
                  if (num >= 1 && num <= product.stock) {
                    setQuantity(num);
                  }
                }}
                keyboardType="numeric"
                maxLength={3}
              />
              
              <TouchableOpacity
                style={styles.quantityButton}
                onPress={() => handleQuantityChange(1)}
                disabled={quantity >= product.stock}
              >
                <Text style={[
                  styles.quantityButtonText,
                  quantity >= product.stock && styles.quantityButtonDisabled
                ]}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Estado del carrito */}
          {isProductInCart && (
            <View style={styles.cartStatusContainer}>
              <Text style={styles.cartStatusText}>
                ✅ Ya tienes {cartItem?.cantidad} {cartItem?.cantidad === 1 ? 'unidad' : 'unidades'} en el carrito
              </Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Botones de acción */}
      <View style={styles.actionsContainer}>
        <TouchableOpacity
          style={[styles.actionButton, styles.addToCartButton, isAddingToCart && { opacity: 0.7 }]}
          onPress={handleAddToCart}
          disabled={product.stock === 0 || isAddingToCart}
        >
          <Text style={styles.actionButtonText}>
            {isAddingToCart 
              ? '⏳ Agregando...' 
              : product.stock === 0 
                ? 'Sin Stock' 
                : '🛒 Agregar al Carrito'
            }
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.actionButton, styles.buyNowButton]}
          onPress={handleBuyNow}
          disabled={product.stock === 0}
        >
          <Text style={styles.actionButtonText}>
            {product.stock === 0 ? 'Sin Stock' : '⚡ Comprar Ahora'}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  errorText: {
    fontSize: 18,
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#e74c3c',
    fontWeight: '500',
  },
  favoriteButton: {
    padding: 8,
  },
  favoriteButtonText: {
    fontSize: 24,
  },
  imageContainer: {
    backgroundColor: 'white',
    alignItems: 'center',
    paddingVertical: 20,
  },
  productImage: {
    width: width - 40,
    height: width - 40,
    borderRadius: 12,
    backgroundColor: '#f0f0f0',
  },
  infoContainer: {
    backgroundColor: 'white',
    padding: 20,
    marginTop: 8,
  },
  productName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  productCategory: {
    fontSize: 16,
    color: '#666',
    marginBottom: 16,
  },
  priceContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  productPrice: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  stockContainer: {
    backgroundColor: '#f1f3f4',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  stockText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  descriptionContainer: {
    marginBottom: 20,
  },
  descriptionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  sellerContainer: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  sellerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  sellerName: {
    fontSize: 16,
    color: '#666',
  },
  quantityContainer: {
    marginBottom: 16,
  },
  quantityLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quantityButton: {
    width: 40,
    height: 40,
    backgroundColor: '#f1f3f4',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quantityButtonText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  quantityButtonDisabled: {
    color: '#ccc',
  },
  quantityInput: {
    backgroundColor: '#f1f3f4',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginHorizontal: 12,
    minWidth: 60,
  },
  cartStatusContainer: {
    backgroundColor: '#e8f5e8',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  cartStatusText: {
    fontSize: 14,
    color: '#2d5a2d',
    textAlign: 'center',
  },
  actionsContainer: {
    backgroundColor: 'white',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    gap: 12,
  },
  actionButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addToCartButton: {
    backgroundColor: '#f1f3f4',
    borderWidth: 2,
    borderColor: '#e74c3c',
  },
  buyNowButton: {
    backgroundColor: '#e74c3c',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
});

export default ProductDetailScreen;
